/**
 * Finanzas → Izipay (`/finanzas/izipay`).
 *
 * Portado de `Izipay` + `views/izipay/index.php` y de su tabla de DataTables con
 * `Izipay/ajax_list`.
 *
 * La pantalla es el **libro de las transacciones del POS**: los cobros con tarjeta
 * que el terminal de Izipay deja en `success_transactions`. Se consultan por rango
 * de fechas —con el **día en curso** por defecto, como el original—, con sus
 * contadores, el desglose por forma de lectura, la ficha de cada cobro con la
 * respuesta completa del POS, el ticket para imprimir, las exportaciones y la
 * **anulación**, que sólo ve un administrador.
 *
 * Diferencias anotadas:
 * - El buscador va paginado y filtrado en el servidor, y mira también el POS, el
 *   terminal, el comercio, el lote y el monto (el original sólo referencia,
 *   autorización y últimos dígitos).
 * - Se añaden los **rangos rápidos** (hoy, 7 días, mes y año) porque el rango por
 *   defecto es el día en curso, como el original.
 * - La exportación sale en **CSV y PDF** (el original sólo Excel) con las mismas
 *   columnas, y el comprobante se genera como **ticket en PDF** en lugar de la
 *   vista de impresión.
 * - El desglose por día del cuadro de mando del original se calcula en la API; la
 *   pantalla muestra el **por método**, que es el que se consulta a diario.
 */

import {
  Ban,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Eye,
  FileSpreadsheet,
  FileText,
  Info,
  QrCode,
  RefreshCw,
  Search,
  ShieldOff,
  Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, toIsoDate } from '@/lib/utils';

import { DialogoTransaccionIzipay } from '../components/izipay-dialogos';
import { izipayApi, useAnularIzipay, useIzipay, useTransaccionIzipay } from '../izipay.api';
import type { FiltrosIzipay } from '../izipay.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Tonos del distintivo según el resultado del cobro. */
const TONOS: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  success: 'success',
  partial: 'warning',
  failed: 'danger',
  reversed: 'neutral',
};

/** Fecha y hora local en el formato del campo (`AAAA-MM-DDTHH:MM`). */
function momentoLocal(fecha: Date): string {
  const dia = toIsoDate(fecha);
  const hora = String(fecha.getHours()).padStart(2, '0');
  const minuto = String(fecha.getMinutes()).padStart(2, '0');

  return `${dia}T${hora}:${minuto}`;
}

/** Los rangos rápidos de la cabecera de filtros. */
function rangoRapido(clave: 'hoy' | 'semana' | 'mes' | 'anio'): { desde: string; hasta: string } {
  const ahora = new Date();
  const inicio = new Date(ahora);

  if (clave === 'semana') inicio.setDate(ahora.getDate() - 6);
  if (clave === 'mes') inicio.setDate(1);
  if (clave === 'anio') inicio.setMonth(0, 1);

  inicio.setHours(0, 0, 0, 0);

  return { desde: momentoLocal(inicio), hasta: momentoLocal(ahora) };
}

export function IzipayPage() {
  useDocumentTitle('Finanzas · Izipay');

  const [rango, setRango] = useState(() => rangoRapido('hoy'));
  const [metodo, setMetodo] = useState('');
  const [pos, setPos] = useState('');
  const [estado, setEstado] = useState('');
  const [anulado, setAnulado] = useState('');
  const [marca, setMarca] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(25);
  const [abierta, setAbierta] = useState<number | null>(null);
  const [porAnular, setPorAnular] = useState<{ id: number; etiqueta: string } | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filtros = useMemo<FiltrosIzipay>(
    () => ({
      desde: rango.desde,
      hasta: rango.hasta,
      metodo,
      pos,
      estado,
      anulado,
      marca,
      buscar: busqueda.trim(),
      page: pagina,
      perPage: limite,
    }),
    [rango, metodo, pos, estado, anulado, marca, busqueda, pagina, limite],
  );

  const consulta = useIzipay(filtros);
  const detalle = useTransaccionIzipay(abierta);
  const anular = useAnularIzipay();

  const transacciones = consulta.data?.transacciones ?? [];
  const totales = consulta.data?.totales;
  const total = consulta.data?.total ?? 0;
  const paginas = consulta.data?.paginas ?? 1;
  const porMetodo = consulta.data?.por_metodo ?? [];
  const mayorMonto = Math.max(...porMetodo.map((item) => item.monto), 1);

  /** Totales de la página, como el pie de la tabla del original. */
  const pieDePagina = useMemo(
    () => ({
      monto: (consulta.data?.transacciones ?? []).reduce(
        (suma, fila) => suma + (fila.anulado ? 0 : fila.monto),
        0,
      ),
      anulado: (consulta.data?.transacciones ?? []).reduce(
        (suma, fila) => suma + (fila.anulado ? fila.monto : 0),
        0,
      ),
    }),
    [consulta.data?.transacciones],
  );

  /** Cambiar cualquier filtro vuelve a la primera página. */
  const filtrar = (cambio: () => void) => {
    setPagina(1);
    cambio();
  };

  const aplicarRango = (clave: 'hoy' | 'semana' | 'mes' | 'anio') => {
    filtrar(() => setRango(rangoRapido(clave)));
  };

  const exportar = async (formato: 'csv' | 'pdf') => {
    setError(null);

    try {
      await abrirPdfAutenticado(izipayApi.urlExportar(filtros, formato), {
        nombre: `izipay-${rango.desde.slice(0, 10)}_${rango.hasta.slice(0, 10)}.${formato}`,
      });
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo generar la exportación.');
    }
  };

  const confirmarAnulacion = () => {
    if (!porAnular) return;

    setError(null);

    anular.mutate(
      { id: porAnular.id, dispositivo: navigator.userAgent.slice(0, 100) },
      {
        onSuccess: (resultado) => {
          setAviso(resultado.message);
          setPorAnular(null);
          setTimeout(() => setAviso(null), 9000);
        },
        onError: (fallo) => {
          setError(fallo instanceof Error ? fallo.message : 'No se pudo anular la transacción.');
          setPorAnular(null);
        },
      },
    );
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Izipay</h1>
          <p className="mt-1 text-sm text-ink-500">
            Las transacciones del terminal de Izipay: cada cobro con tarjeta con su autorización, su
            lote y la respuesta completa del POS.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<FileSpreadsheet className="size-4" aria-hidden="true" />}
            onClick={() => void exportar('csv')}
          >
            Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
            onClick={() => void exportar('pdf')}
          >
            PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="No se pudo completar la acción">
          {error}
        </Alert>
      ) : null}

      {/* Filtros */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
              Rango rápido
            </span>
            {(
              [
                ['hoy', 'Hoy'],
                ['semana', '7 días'],
                ['mes', 'Este mes'],
                ['anio', 'Este año'],
              ] as const
            ).map(([clave, etiqueta]) => (
              <Button
                key={clave}
                variant="outline"
                size="sm"
                onClick={() => aplicarRango(clave)}
              >
                {etiqueta}
              </Button>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Fecha y hora desde
              <CampoFecha
                tipo="datetime-local"
                value={rango.desde}
                max={rango.hasta}
                onChange={(evento) =>
                  filtrar(() => setRango({ ...rango, desde: evento.target.value }))
                }
                aria-label="Fecha y hora desde"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Fecha y hora hasta
              <CampoFecha
                tipo="datetime-local"
                value={rango.hasta}
                min={rango.desde}
                onChange={(evento) =>
                  filtrar(() => setRango({ ...rango, hasta: evento.target.value }))
                }
                aria-label="Fecha y hora hasta"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Método
              <Select
                value={metodo}
                onChange={(evento) => filtrar(() => setMetodo(evento.target.value))}
                className={CONTROL}
                aria-label="Método"
              >
                <option value="">Todos los métodos</option>
                {(consulta.data?.metodos ?? []).map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              POS
              <Select
                value={pos}
                onChange={(evento) => filtrar(() => setPos(evento.target.value))}
                className={CONTROL}
                aria-label="POS"
              >
                <option value="">Todos los POS</option>
                {(consulta.data?.pos ?? []).map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Estado
              <Select
                value={estado}
                onChange={(evento) => filtrar(() => setEstado(evento.target.value))}
                className={CONTROL}
                aria-label="Estado"
              >
                <option value="">Todos los estados</option>
                {(consulta.data?.estados ?? []).map((item) => (
                  <option key={item.valor} value={item.valor}>
                    {item.etiqueta}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Anulado
              <Select
                value={anulado}
                onChange={(evento) => filtrar(() => setAnulado(evento.target.value))}
                className={CONTROL}
                aria-label="Anulado"
              >
                <option value="">Todas</option>
                <option value="0">Sólo vigentes</option>
                <option value="1">Sólo anuladas</option>
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Marca
              <Select
                value={marca}
                onChange={(evento) => filtrar(() => setMarca(evento.target.value))}
                className={CONTROL}
                aria-label="Marca"
              >
                <option value="">Todas las marcas</option>
                {(consulta.data?.marcas ?? []).map((item) => (
                  <option key={item.marca} value={item.marca}>
                    {item.marca} ({item.cantidad})
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Filas
              <Select
                value={String(limite)}
                onChange={(evento) => filtrar(() => setLimite(Number(evento.target.value)))}
                className={CONTROL}
                aria-label="Filas por página"
              >
                {(consulta.data?.tamanos ?? [10, 25, 50, 100]).map((tamano) => (
                  <option key={tamano} value={String(tamano)}>
                    {tamano}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-xs font-medium text-ink-500">
              Buscar
              <span className="relative block">
                <Search
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <Input
                  value={busqueda}
                  onChange={(evento) => filtrar(() => setBusqueda(evento.target.value))}
                  placeholder="Referencia, autorización, últimos dígitos, POS, lote, monto…"
                  className="pl-9"
                  aria-label="Buscar transacción"
                />
              </span>
            </label>

            <span className="ml-auto text-xs text-ink-500">
              {total.toLocaleString('es-PE')} transacción{total === 1 ? '' : 'es'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Contadores del rango */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600"
            >
              <CreditCard className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Transacciones
              </p>
              <p className="text-base font-bold break-words text-ink-900 tabular-nums sm:text-lg">
                {(totales?.transacciones ?? 0).toLocaleString('es-PE')}
              </p>
              <p className="text-xs text-ink-500">
                {(totales?.vigentes ?? 0).toLocaleString('es-PE')} vigente(s)
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"
            >
              <Wallet className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Monto vigente
              </p>
              <p className="text-base font-bold break-words text-emerald-800 tabular-nums sm:text-lg">
                {formatCurrency(totales?.monto_vigente ?? 0)}
              </p>
              <p className="text-xs text-ink-500">
                total {formatCurrency(totales?.monto_total ?? 0)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-ink-100 text-ink-600"
            >
              <QrCode className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Formas de lectura
              </p>
              <p className="text-sm font-semibold text-ink-700">
                QR {totales?.qr ?? 0} · Contactless {totales?.contactless ?? 0}
              </p>
              <p className="text-xs text-ink-500">Chip {totales?.chip ?? 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className={cn((totales?.anuladas ?? 0) > 0 && 'border-rose-200 bg-rose-50/50')}>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-600"
            >
              <ShieldOff className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Anuladas
              </p>
              <p className="text-base font-bold break-words text-rose-700 tabular-nums sm:text-lg">
                {formatCurrency(totales?.monto_anulado ?? 0)}
              </p>
              <p className="text-xs text-ink-500">
                {(totales?.anuladas ?? 0).toLocaleString('es-PE')} transacción(es)
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Desglose por método */}
      {porMetodo.length > 0 ? (
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por forma de lectura</h2>

            <div className="space-y-2">
              {porMetodo.map((fila) => (
                <div key={fila.metodo} className="flex flex-wrap items-center gap-3">
                  <span className="w-44 shrink-0 text-xs font-medium text-ink-600">
                    {fila.metodo}
                  </span>
                  <span className="h-2.5 min-w-[6rem] flex-1 overflow-hidden rounded-full bg-ink-100">
                    <span
                      className="block h-full rounded-full bg-brand-500"
                      style={{ width: `${Math.max(2, (fila.monto / mayorMonto) * 100)}%` }}
                    />
                  </span>
                  <span className="shrink-0 text-xs font-semibold text-ink-700 tabular-nums">
                    {formatCurrency(fila.monto)}
                  </span>
                  <span className="w-20 shrink-0 text-right text-xs text-ink-500 tabular-nums">
                    {fila.transacciones.toLocaleString('es-PE')} op.
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Listado */}
      <Card>
        <CardContent className="space-y-3 p-4">
          {consulta.isError ? (
            <Alert variant="error" title="No se pudieron leer las transacciones">
              {consulta.error instanceof ApiError
                ? consulta.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2 whitespace-nowrap">ID</th>
                  <th className="px-2 py-2 whitespace-nowrap">Fecha</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Monto</th>
                  <th className="px-2 py-2">Método</th>
                  <th className="px-2 py-2">POS</th>
                  <th className="px-2 py-2">Referencia</th>
                  <th className="px-2 py-2 whitespace-nowrap">Tarjeta</th>
                  <th className="px-2 py-2 whitespace-nowrap">Autorización</th>
                  <th className="px-2 py-2 whitespace-nowrap">Lote</th>
                  <th className="px-2 py-2">Estado</th>
                  <th className="px-2 py-2">Marca</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {consulta.isLoading
                  ? Array.from({ length: 6 }).map((_, fila) => (
                      <tr key={fila} className="border-t border-ink-100">
                        {Array.from({ length: 12 }).map((__, columna) => (
                          <td key={columna} className="px-2 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : transacciones.map((fila) => (
                      <tr
                        key={fila.id}
                        className={cn(
                          'border-t border-ink-100',
                          fila.anulado && 'bg-rose-50/40 text-ink-500',
                        )}
                      >
                        <td className="px-2 py-2 font-semibold text-ink-800 tabular-nums">
                          {fila.id}
                        </td>

                        <td className="px-2 py-2 whitespace-nowrap">
                          <p>{formatDateValue(fila.registrado?.slice(0, 10))}</p>
                          <p className="text-[11px] text-ink-500">
                            {fila.registrado?.slice(11, 16) ?? '—'}
                          </p>
                        </td>

                        <td
                          className={cn(
                            'px-2 py-2 text-right font-semibold whitespace-nowrap tabular-nums',
                            fila.anulado ? 'text-ink-400 line-through' : 'text-emerald-700',
                          )}
                        >
                          {formatCurrency(fila.monto)}
                        </td>

                        <td className="px-2 py-2 whitespace-nowrap">{fila.metodo}</td>

                        <td className="max-w-[10rem] px-2 py-2">
                          <span className="block truncate" title={fila.pos}>
                            {fila.pos}
                          </span>
                        </td>

                        <td className="px-2 py-2 whitespace-nowrap">{fila.referencia || '—'}</td>

                        <td className="px-2 py-2 whitespace-nowrap">
                          {fila.ultimos_digitos ? `***${fila.ultimos_digitos}` : '—'}
                        </td>

                        <td className="px-2 py-2 whitespace-nowrap">{fila.autorizacion || '—'}</td>

                        <td className="px-2 py-2 whitespace-nowrap">{fila.lote || '—'}</td>

                        <td className="px-2 py-2">
                          {fila.anulado ? (
                            <Badge variant="danger">Anulada</Badge>
                          ) : (
                            <Badge variant={TONOS[fila.estado] ?? 'neutral'}>
                              {fila.estado_etiqueta}
                            </Badge>
                          )}
                        </td>

                        <td className="px-2 py-2">
                          {fila.marca ? (
                            <Badge variant="neutral">{fila.marca}</Badge>
                          ) : (
                            <span className="text-ink-300">—</span>
                          )}
                        </td>

                        <td className="px-2 py-2">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Ver la transacción"
                              aria-label={`Ver la transacción ${fila.id}`}
                              onClick={() => {
                                setError(null);
                                setAbierta(fila.id);
                              }}
                            >
                              <Eye className="size-4" aria-hidden="true" />
                            </Button>

                            {!fila.anulado && consulta.data?.puede_anular ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Anular la transacción"
                                aria-label={`Anular la transacción ${fila.id}`}
                                onClick={() =>
                                  setPorAnular({
                                    id: fila.id,
                                    etiqueta: fila.referencia
                                      ? `${fila.referencia} (S/ ${fila.monto.toFixed(2)})`
                                      : `#${fila.id}`,
                                  })
                                }
                              >
                                <Ban className="size-4 text-rose-600" aria-hidden="true" />
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
              </tbody>
              {!consulta.isLoading && transacciones.length > 0 ? (
                <tfoot>
                  <tr className="border-t border-ink-200 bg-ink-50/60 text-[12px] font-semibold text-ink-700">
                    <td className="px-2 py-2" colSpan={2}>
                      Totales de la página ({transacciones.length} fila(s))
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {formatCurrency(pieDePagina.monto)}
                    </td>
                    <td className="px-2 py-2" colSpan={7}>
                      {pieDePagina.anulado > 0
                        ? `Anulado: ${formatCurrency(pieDePagina.anulado)}`
                        : ''}
                    </td>
                    <td className="px-2 py-2" colSpan={2} />
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-ink-500">
              Página {pagina} de {paginas.toLocaleString('es-PE')}
              {total > 0
                ? ` · mostrando ${((pagina - 1) * limite + 1).toLocaleString('es-PE')}–${Math.min(
                    pagina * limite,
                    total,
                  ).toLocaleString('es-PE')}`
                : ''}
            </span>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagina <= 1}
                leftIcon={<ChevronLeft className="size-4" aria-hidden="true" />}
                onClick={() => setPagina((actual) => Math.max(1, actual - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagina >= paginas}
                rightIcon={<ChevronRight className="size-4" aria-hidden="true" />}
                onClick={() => setPagina((actual) => actual + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Las transacciones las registra el terminal de Izipay conforme cobra; aquí se consultan, se
        imprime su ticket y —sólo un administrador— se anulan. Anular no revierte el cargo en la
        tarjeta: eso se hace desde el propio terminal.
      </p>

      <DialogoTransaccionIzipay
        key={`transaccion-${abierta ?? 'cerrada'}`}
        transaccionId={abierta}
        consulta={detalle.data}
        cargando={detalle.isLoading}
        guardando={anular.isPending}
        error={abierta ? error : null}
        onCerrar={() => {
          setAbierta(null);
          setError(null);
        }}
        onAnular={(id, etiqueta) => setPorAnular({ id, etiqueta })}
      />

      <ConfirmDialog
        open={Boolean(porAnular)}
        title="Anular la transacción"
        description={
          porAnular
            ? `Se marcará como anulada la transacción ${porAnular.etiqueta}. El cargo no se revierte en la tarjeta: eso se hace desde el terminal de Izipay.`
            : ''
        }
        confirmLabel="Anular transacción"
        loading={anular.isPending}
        onConfirm={confirmarAnulacion}
        onClose={() => setPorAnular(null)}
      />
    </div>
  );
}
